// contexts/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth'
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore'
import { app } from '../firebase'
import { getAuthErrorMessage } from '../utils/authErrors'

const AuthContext = createContext()

export function useAuth () {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider ({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const auth = getAuth(app)
  const db = getFirestore(app)

  // Registrierung mit zusätzlichen Nutzerdaten
  const register = async (
    email,
    password,
    displayName,
    additionalData = {}
  ) => {
    try {
      setError(null)
      setLoading(true)

      // Firebase Auth User erstellen
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      )

      // Profil updaten
      await updateProfile(user, { displayName })

      // Zusätzliche Nutzerdaten in Firestore speichern (global)
      // Note: companyId and role will be set by useInviteCode backend function
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: displayName,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        ...additionalData
      })

      return user
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error)
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Anmeldung
  const login = async (email, password) => {
    try {
      setError(null)
      setLoading(true)

      const { user } = await signInWithEmailAndPassword(auth, email, password)

      // Letzten Login-Zeitpunkt aktualisieren in globaler users collection
      await setDoc(
        doc(db, 'users', user.uid),
        {
          lastLoginAt: serverTimestamp()
        },
        { merge: true }
      )

      // Auch in company users collection aktualisieren (wenn companyId vorhanden)
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        if (userData.companyId) {
          await setDoc(
            doc(db, 'companies', userData.companyId, 'users', user.uid),
            {
              lastLoginAt: serverTimestamp()
            },
            { merge: true }
          )
        }
      }

      return user
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error)
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Abmeldung
  const logout = async () => {
    try {
      setError(null)
      await signOut(auth)
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error)
      setError(errorMessage)
      throw error
    }
  }

  // Nutzerdaten aus Firestore laden
  const getUserData = async uid => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid))
      return userDoc.exists() ? userDoc.data() : null
    } catch (error) {
      console.error('Fehler beim Laden der Nutzerdaten:', error)
      return null
    }
  }

  // Auth State Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        // Erweiterte Nutzerdaten aus Firestore laden
        const userData = await getUserData(user.uid)
        console.log('🔐 User data loaded:', {
          email: user.email,
          role: userData?.role,
          company: userData?.company
        })
        setUser({
          ...user,
          userData: userData
        })
        // Set loading to false AFTER userData is loaded
        setLoading(false)
      } else {
        setUser(null)
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    getUserData,
    setError
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
